import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { SharePanelActionKeys, retrieveSubFoldersContent } from './actions';
import { StoreState } from '../../../reducers';
import { PageOverlay } from '../../../components/PageOverlay/styles';
import {
	StyledSharePanel,
	SharePanelHeader, SharePanelHeaderContainer, SharePanelHeaderTitle, SharePanelHeaderClose,
	SharePanelContent, SharePanelContentContainer,
	SharePanelContentSelection, SharePanelContentSelectionTitle, SharePanelContentSelectionContent,
	SharePanelContentRight, SharePanelContentForm,
	SharePanelContentFormTitle, SharePanelContentFormContent, SharePanelContentFormContentLeft, SharePanelContentFormContentRight,
	ShareButton, ShareInput, ShareInputLast
} from './styles';
import { TextArea } from '../../../components/Form/styles';
import { SharePanelState } from './reducer';
import { GalleryInfosState } from '../../Gallery/reducer';
import UrlUtils from '../../../utils/url';
import { Api } from '../../../typings/api';
import ApiUtils from '../../../utils/api';

const Translate = require('react-redux-i18n').Translate;
const I18n = require('react-redux-i18n').I18n;

export interface SharePanelProps {
	sharePanel: SharePanelState;
	galleryInfos: GalleryInfosState;
	dispatch: Dispatch<StoreState>;
}

export interface SharedData {
	fromName: string;
	fromEmail: string;
	toAddresses: string[];
	message: string;
	language: string;
	resources: SharedResource[];
}

interface SharedResource {
	id: number;
	url: string;
}

export class SharePanel extends React.Component<SharePanelProps, {}> {

	constructor(props: SharePanelProps) {
		super(props);
		this.handleClickClose = this.handleClickClose.bind(this);
		this.handleChangeFormField = this.handleChangeFormField.bind(this);
		this.handleSendForm = this.handleSendForm.bind(this);
	}

	componentWillUpdate(nextProps: SharePanelProps) {
		const { sharePanel, dispatch } = this.props;
		const { selection } = nextProps.sharePanel;
		const prevSelection = sharePanel.selection;

		if (selection !== prevSelection) {
			selection.forEach(item => {
				if (item.type === Api.DriveItemType.Directory) {
					dispatch(retrieveSubFoldersContent(item.id));
				}
			});
		}
	}

	handleClickClose() {
		const { dispatch } = this.props;
		dispatch(
			{
				type: SharePanelActionKeys.REQUEST_CLOSE
			}
		);
	}

	handleOverlayClick = () => {
		const { dispatch } = this.props;
		dispatch({ type: SharePanelActionKeys.REQUEST_CLOSE });
	}

	handleChangeFormField(name: string) {
		return (e: React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLTextAreaElement>) => {
			const { dispatch } = this.props;
			dispatch(
				{
					type: SharePanelActionKeys.SET_FORM_FIELD,
					name: name,
					value: e.currentTarget.value
				}
			);
		};
	}

	handleSendForm(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const { galleryInfos, sharePanel } = this.props;
		const galleryConfig = galleryInfos.config;
		const { locale } = galleryConfig;
		const { form } = sharePanel;
		const { name, userEmail, contactEmail, message } = form;

		const requestData = {
			fromName: name,
			fromEmail: userEmail,
			toAddresses: [contactEmail],
			message,
			language: locale,
			resources: this.retrieveResources()
		};

		ApiUtils.shareContent(requestData)
			.then(response => this.handleClickClose());
	}

	render() {
		const { isOpen } = this.props.sharePanel;

		return (
			<div>
				<PageOverlay isOpen={isOpen} onClick={this.handleOverlayClick} />
				<StyledSharePanel
					isOpen={isOpen}
				>
					{this.renderHeader()}
					{this.renderContent()}
				</StyledSharePanel>
			</div>
		);
	}

	renderHeader() {
		return (
			<SharePanelHeader>
				<SharePanelHeaderContainer>
					<SharePanelHeaderTitle>
						<Translate
							value="messages.share_my_selection"
						/>
					</SharePanelHeaderTitle>
					<SharePanelHeaderClose onClick={this.handleClickClose}>
						<Translate
							value="messages.close"
						/>
					</SharePanelHeaderClose>
				</SharePanelHeaderContainer>
			</SharePanelHeader>
		);
	}

	renderContent() {
		return (
			<SharePanelContent>
				<SharePanelContentContainer>
					<SharePanelContentSelection>
						<SharePanelContentSelectionTitle>
							<Translate
								value="messages.my_selection"
							/>
						</SharePanelContentSelectionTitle>
						{this.renderSelectionContent()}
					</SharePanelContentSelection>
					<SharePanelContentRight>
						<SharePanelContentForm>
							<SharePanelContentFormTitle>
								<Translate
									value="messages.share_by_email"
								/>
							</SharePanelContentFormTitle>
							<SharePanelContentFormContent
								onSubmit={this.handleSendForm}
							>
								{this.renderFormLeft()}
								{this.renderFormRight()}
							</SharePanelContentFormContent>
						</SharePanelContentForm>
					</SharePanelContentRight>
				</SharePanelContentContainer>
			</SharePanelContent>
		);
	}

	renderSelectionContent() {
		const { selection } = this.props.sharePanel;

		return selection.map(
			(driveItem, index) => (
				<SharePanelContentSelectionContent
					key={index}
				>
					{driveItem.label}
				</SharePanelContentSelectionContent>
			)
		);
	}

	retrieveResources(): SharedResource[] {
		const { galleryInfos, sharePanel } = this.props;
		const galleryConfig = galleryInfos.config;
		const { selection, foldersContent } = sharePanel;
		const sharedContent = [...selection.filter(item => item.type !== Api.DriveItemType.Directory), ...foldersContent];

		return sharedContent.map(item => {
			const { type, id, originalFile, src } = item;
			let url = '';

			switch (type) {
				case Api.DriveItemType.Gallery:
				case Api.DriveItemType.AppContainer:
					url = `${UrlUtils.getConfiguredRootApiClient(galleryConfig)}/${id}`;
					break;
				case Api.DriveItemType.Publication:
					url = UrlUtils.getPublicationUrl(galleryConfig, item, 1, false, true);
					break;
				case Api.DriveItemType.ExternalLink:
					const linkId = src.replace('$media$', '');
					url = `${UrlUtils.getConfiguredRootApiClient(galleryConfig)}/${linkId}`;
					break;
				default:
					url = `${UrlUtils.getConfiguredRootApiClient(galleryConfig)}/${originalFile}`;
					break;
			}

			return { id, url };
		});
	}

	renderFormLeft() {
		const { form } = this.props.sharePanel;
		const { name, userEmail, contactEmail } = form;

		return (
			<SharePanelContentFormContentLeft>
				<ShareInput
					value={name}
					onChange={this.handleChangeFormField('name')}
					placeholder={I18n.t('messages.placeholder_share_name')}
					required={true}
					type="text"
					key="name"
				/>
				<ShareInput
					value={userEmail}
					onChange={this.handleChangeFormField('userEmail')}
					placeholder={I18n.t('messages.placeholder_share_user_email')}
					required={true}
					type="email"
					key="userEmail"
				/>
				<ShareInputLast
					value={contactEmail}
					onChange={this.handleChangeFormField('contactEmail')}
					placeholder={I18n.t('messages.placeholder_share_contact_email')}
					required={true}
					type="email"
					key="contactEmail"
				/>
			</SharePanelContentFormContentLeft>
		);
	}

	renderFormRight() {
		const { form } = this.props.sharePanel;
		const { message } = form;

		return (
			<SharePanelContentFormContentRight>
				<TextArea
					value={message}
					onChange={this.handleChangeFormField('message')}
					placeholder={I18n.t('messages.placeholder_share_message')}
					required={true}
					key="message"
				/>
				<ShareButton
					type="submit"
				>
					<Translate
						value="messages.send_my_selection"
					/>
				</ShareButton>
			</SharePanelContentFormContentRight>
		);
	}
}

const mapStateToProps = (state: StoreState) => {
	const { sharePanel, galleryInfos } = state;
	return { sharePanel, galleryInfos };
};

export default connect(mapStateToProps)(SharePanel);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Share/Panel/index.tsx